import { PATHS } from '@ctcells/core'

import type { GroupTabDef } from '../groupTabs.ts'

/**
 * 관리 화면의 탭 4종 — **유일한 출처다.**
 *
 * 🔴 모듈·로그는 사이드바 「시스템」 그룹에 있다가 2026-09-18 eric 지시로 관리 안으로 들어왔다
 *    ("모듈과 로그를 관리 메뉴에 다 넣어줘"). 화면 3곳(관리·모듈·로그)이 **같은 탭 줄**을 그리므로
 *    목록을 여기 한 곳에만 둔다 — 세 군데 적으면 반드시 어긋난다.
 *
 * 🔴 **탭이 곧 주소다.** `useState` 로 들고 있으면 ⓐ 새로고침에 탭이 초기화되고
 *    ⓑ 상세 화면에서 「← 모듈」로 돌아올 때 엉뚱한 탭이 열린다(사용자 탭에서 실제로 겪었다).
 *    주소와 묶으면 둘 다 구조적으로 안 생긴다.
 *
 * 🔴 **권한은 탭마다 다르다.** 사이드바 문은 가장 약한 권한(`monitor`)이고, 문을 통과한 뒤
 *    각 탭이 제 권한을 건다. 권한 없는 탭은 **숨기지 않고 비활성 + 사유**다(CLAUDE.md RBAC).
 * ⚠ 여기 값은 **노출 판단 전용**이다. 집행은 서버 403 `INSUFFICIENT_ROLE` 이고,
 *   프론트에서 막았다는 것이 안전을 보장하지 않는다.
 */
export type AdminTabId = 'members' | 'webhooks' | 'general' | 'modules' | 'logs'

/**
 * 순서 = 화면 표시 순서 — 모듈 · 로그 · 웹훅 · 사용자 · 설정(eric 2026-10-28 — 「웹훅」 탭을 새로 내고 「웹훅을 사용자 앞으로」).
 *
 * 🔴 **「자원 점유」 탭이 2026-09-22 에 빠졌다**(eric 「관리에 자원 점유를 설정 탭 안에 넣을 수
 *    있어?」). 화면이 없어진 것이 아니라 **설정 탭의 첫 절**로 옮겼다
 *    (`GeneralSettings`). 2026-10-11 에 그 절이 「걸린 것」(`StopsSection` · `GET /safety/stops`)으로 바뀌었다.
 *    옛 주소 `PATHS.adminResourceLocks` 와 새 `PATHS.adminStops` 는 **살아 있고** 그 절로 보내는
 *    리다이렉트다 — 작업 상세의 「걸린 것 보기」가 그 길이다.
 *    ⇒ 탭으로 되돌리지 마라. 되돌리면 같은 표가 두 자리에 선다.
 * ⚠ 사이드바 「관리」가 여는 주소는 `PATHS.modules`(모듈 탭)다 — eric 2026-10-02 「모듈 탭을 기본 탭으로」.
 *    종전(2026-10-01 까지)은 `PATHS.admin`(사용자 탭)이었다. `PATHS.admin` 주소 자체는 사용자 탭으로 남는다
 */
export const ADMIN_TABS: readonly GroupTabDef<AdminTabId>[] = [
  // 라벨은 사이드바에 쓰던 키를 그대로 재사용한다 — 같은 화면이라 문구가 갈리면 안 된다
  { id: 'modules', path: PATHS.modules, labelKey: 'nav.item.modules', permission: 'monitor' },
  /*
   * 🔴 `monitor` → `view` (2026-09-21). 이 탭이 쓰는 세 경로가 **전부 operator/admin** 이다 —
   *    `GET /logs`(`views/logs.py` `ALLOWED_ROLES = {admin, operator}`) ·
   *    `GET /history` ×2(`views/history.py` `PRIVILEGED_ROLES`). PII 접촉면이라 그렇다
   *    (정본 `07 §2.4` 「이력·로그·시료 보고서·분주 기록 조회 viewer ✗」).
   *    `monitor` 로 두면 viewer 에게 탭이 **열린 채로** 보이고 눌러야 403 을 만난다 —
   *    규약은 **비활성 + 사유**다. 모듈 탭은 `GET /modules` 가 전원 허용이라 `monitor` 그대로다.
   */
  { id: 'logs', path: PATHS.logs, labelKey: 'nav.item.logs', permission: 'view' },
  /*
   * 🔴 **문이 `settings` 에서 `monitor` 로 내려갔다**(2026-09-22). 「자원 점유」(2026-10-11 부터 「걸린 것」)가
   *    이 탭 안으로 들어오면서 **탭 안의 절들이 서로 다른 권한**을 갖게 됐기 때문이다 — 「걸린 것」은 서버가
   *    `GET /safety/stops` 를 역할로 가르지 않아 **viewer 도 본다**
   *    (`views/stops.py` `require_roles(ROLES)`). 문을 `settings` 로 두면 그 절이 admin 에게만
   *    보여 **서버가 200 을 주는 것을 화면이 막는** 꼴이 된다.
   * 🔴 「문은 약한 권한, 안은 각자」 — 값을 고치는 절(운영 모드 · 비밀번호 정책 · 세션 ·
   *    제어락 · 임계값 · 기록)은 제 권한을 제가 건다(`GeneralSettings`). 권한이 없으면
   *    **숨기지 않고 읽기 전용 + 사유**다.
   * ⚠ 장비 관점 콘솔은 제 목록(`EQUIPMENT_ADMIN_TABS`)을 넘기고 거기선 이 탭이 `settings` 다 —
   *   그 앱에는 「걸린 것」으로 오는 길이 없어 문을 내릴 까닭이 없다.
   */
  /*
   * 🔴 **웹훅 탭 — 종전 「설정」 탭의 내용 전부**(등록된 웹훅 + 알람별 전달 · eric 2026-10-28). 문은 그 탭과 같은 `monitor` 이고
   *    안의 조회 · 조작은 admin 전용(`settings`)이라 operator · viewer 에게는 구역이 「admin 전용」 사유로 선다(`AlarmSettingsContainer`).
   */
  {
    id: 'webhooks',
    path: PATHS.adminWebhooks,
    labelKey: 'tab.admin.webhooks',
    permission: 'monitor',
  },
  { id: 'members', path: PATHS.admin, labelKey: 'tab.admin.members', permission: 'settings' },
  /* ⚠ 설정 탭은 2026-10-28 부터 빈 판이다(내용은 eric 이 곧 요청) — 웹훅 내용은 위 탭으로 옮겼다 */
  {
    id: 'general',
    path: PATHS.adminGeneral,
    labelKey: 'tab.admin.general',
    permission: 'monitor',
  },
]

/**
 * 설정 탭 안 「걸린 것」 절의 닻(2026-10-11 · 종전 「자원 점유」 절 `locks`).
 *
 * 🔴 **`PATHS.adminStops` 와 옛 주소 `PATHS.adminResourceLocks` 가 여기로 온다.** 작업 상세의 복구 대기
 *    구역이 그 경로로 걸어 두었고(그 길이 없어 사람이 반나절을 썼던 자리다 · 2026-09-21), 절을
 *    바꿨다고 그 길까지 끊으면 같은 사고가 난다. 문자열을 화면에서 짓지 마라 — 여기가 출처다.
 */
export const ADMIN_STOPS_ANCHOR = 'stops'

/** 「걸린 것」 절로 바로 가는 주소. 리다이렉트와 닻이 **같은 값**을 쓴다 */
export const adminStopsPath = `${PATHS.adminGeneral}#${ADMIN_STOPS_ANCHOR}`
